import { request } from '../../lib/oem-client';
import { useResumableFields } from '../../lib/session-resume';
import { StockSourcePicker } from './stock-source-picker';
import { stateLabel } from './display-copy';
import { requiredValue, displayValue } from '../../lib/oem-display';
import { useState } from 'react';
import { ProductPicker } from './product-picker';
import {
  productChoiceNotes,
  updateProductNotes,
  hydrateCustomerBrief,
  customerBriefEquivalent,
  updateCustomerServices,
  customerBriefForSave,
  customerBriefBudget,
} from './product-choice-model';
import {
  oemContentContracts,
  oemBundle,
  oemCostRequestContract,
  oemCostRequestContentSchema,
  validateOemContent,
  validateOemPayload,
} from '@sqi/shared-schemas';
import { ContractField, type FieldContract } from './contract-fields';
import { MediaDialog } from './media-dialog';
import type { OemKind } from '@sqi/shared-schemas';
import type { OemRecord } from '../../lib/oem-client';
export interface FormDefinition {
  readonly kind: OemKind;
  readonly title: string;
  readonly fields: readonly {
    readonly key: string;
    readonly label: string;
    readonly type?: 'textarea' | 'decimal' | 'select';
    readonly options?: readonly string[];
  }[];
  readonly userId?: string;
  readonly decisions?: {
    recordId: string;
    action: string;
  }[];
  readonly actions: readonly {
    readonly key: string;
    readonly label: string;
  }[];
}
export const forms: Record<string, FormDefinition> = {
  brief: {
    kind: 'brief',
    title: 'บรีฟที่พัฒนาร่วมกัน',
    fields: [
      { key: 'label', label: 'ชื่อผลิตภัณฑ์' },
      { key: 'category', label: 'ประเภท' },
      { key: 'market', label: 'ตลาดปลายทาง' },
      { key: 'quantity', label: 'จำนวนที่ต้องการ', type: 'decimal' },
      { key: 'fillSize', label: 'ขนาดบรรจุ' },
      { key: 'summary', label: 'ความต้องการและสิ่งที่ยังไม่ทราบ', type: 'textarea' },
    ],
    actions: [{ key: 'submit', label: 'ส่งบรีฟเพื่อทบทวน' }],
  },
  formulas: {
    kind: 'formula',
    title: 'สูตรและหลักฐานวัตถุดิบ',
    fields: [
      { key: 'label', label: 'รหัสสูตร' },
      { key: 'summary', label: 'ฐานสูตรและแหล่งอ้างอิง', type: 'textarea' },
      {
        key: 'densityState',
        label: 'ค่าความหนาแน่น',
        type: 'select',
        options: ['unknown', 'proposed', 'verified'],
      },
      {
        key: 'yieldState',
        label: 'อัตราผลผลิตที่ใช้ได้',
        type: 'select',
        options: ['unknown', 'proposed', 'verified'],
      },
      { key: 'ipSource', label: 'สิทธิ์และขอบเขตการใช้สูตร' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งสูตรให้ผู้ทบทวน' },
      { key: 'review_technical', label: 'ทบทวนทางเทคนิค' },
    ],
  },
  packaging: {
    kind: 'packaging',
    title: 'รูปทรง วัสดุ และรายละเอียดที่เลือก',
    fields: [
      { key: 'label', label: 'รหัสสเปกและเวอร์ชัน' },
      { key: 'component', label: 'ส่วนประกอบ' },
      { key: 'material', label: 'วัสดุ' },
      { key: 'dimensions', label: 'ขนาด / หน่วย / ค่าคลาดเคลื่อน' },
      { key: 'finish', label: 'ผิวและสี' },
      { key: 'summary', label: 'ความเข้ากันได้ สิทธิ์ และหลักฐาน', type: 'textarea' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งสเปก' },
      { key: 'decide_design', label: 'รับเฉพาะรูปแบบ' },
      { key: 'review_technical', label: 'ทบทวนความเข้ากันได้' },
    ],
  },
  sourcing: {
    kind: 'sourcing',
    title: 'แหล่งจัดหาและข้อเท็จจริงที่ยืนยัน',
    fields: [
      { key: 'label', label: 'ส่วนประกอบ / ผู้เสนอ' },
      { key: 'source', label: 'แหล่งข้อมูลและวันที่' },
      { key: 'price', label: 'ราคา', type: 'decimal' },
      { key: 'moq', label: 'MOQ', type: 'decimal' },
      { key: 'multiple', label: 'จำนวนสั่งเป็นชุด', type: 'decimal' },
      {
        key: 'summary',
        label: 'ระยะจัดส่ง / ค่าเครื่องมือ / สิทธิ์ / สิ่งที่ยังไม่ทราบ',
        type: 'textarea',
      },
    ],
    actions: [{ key: 'submit', label: 'ส่งข้อเสนอจัดหา' }],
  },
  reviews: {
    kind: 'review',
    title: 'ทบทวนหลักฐานและขอบเขต',
    fields: [
      { key: 'label', label: 'หัวข้อทบทวน' },
      { key: 'source', label: 'หลักฐาน / แหล่งอ้างอิง' },
      { key: 'scope', label: 'ขอบเขตที่มีคุณสมบัติทบทวน' },
      { key: 'summary', label: 'ผล ข้อจำกัด และงานถัดไป', type: 'textarea' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งหลักฐาน' },
      { key: 'review_technical', label: 'บันทึกผลเทคนิค' },
      { key: 'review_regulatory', label: 'บันทึกผล QA' },
    ],
  },
  tests: {
    kind: 'test_result',
    title: 'ผลทดสอบและการยอมรับแยกกัน',
    fields: [
      { key: 'label', label: 'แผน / วิธี / เวอร์ชัน' },
      { key: 'source', label: 'รายงานและแหล่งอ้างอิง' },
      { key: 'result', label: 'ผล', type: 'select', options: ['pending', 'passed', 'failed'] },
      { key: 'summary', label: 'เกณฑ์ / tolerance / หลักฐาน', type: 'textarea' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งรายงาน' },
      { key: 'review_regulatory', label: 'ทบทวนผลโดยผู้มีคุณสมบัติ' },
    ],
  },
  costs: {
    kind: 'cost',
    title: 'ต้นทุนที่ตรวจสอบแหล่งที่มาได้',
    fields: [
      { key: 'label', label: 'สถานการณ์ / tier' },
      { key: 'source', label: 'แหล่งราคาและวันที่' },
      {
        key: 'summary',
        label: 'ราคาที่ขาด yield ค่าแรง ค่าทดสอบ เครื่องมือ ภาษี',
        type: 'textarea',
      },
    ],
    actions: [
      { key: 'submit', label: 'ส่งต้นทุนให้ผู้ทบทวน' },
      { key: 'review_commercial', label: 'ทบทวนเชิงพาณิชย์' },
    ],
  },
  quotes: {
    kind: 'quote',
    title: 'เงื่อนไขที่ชัด ก่อนตัดสินใจ',
    fields: [
      { key: 'label', label: 'ชื่อใบเสนอราคา' },
      { key: 'quoteType', label: 'ชนิด', type: 'select', options: ['preliminary', 'final'] },
      { key: 'price', label: 'ราคา', type: 'decimal' },
      { key: 'currency', label: 'สกุลเงิน' },
      { key: 'terms', label: 'เงื่อนไขที่ตกลง', type: 'textarea' },
      { key: 'validUntil', label: 'วันหมดอายุ' },
      { key: 'destination', label: 'สถานที่ส่งมอบ' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งร่าง' },
      { key: 'review_commercial', label: 'ทบทวนใบเสนอราคา' },
      { key: 'issue_quote', label: 'ออกใบเสนอราคา' },
      { key: 'accept_quote', label: 'รับใบเสนอราคาสุดท้าย' },
    ],
  },
  po: {
    kind: 'po',
    title: 'ใบสั่งซื้อและจุดที่ต้องเทียบ',
    fields: [
      { key: 'label', label: 'เลข PO' },
      { key: 'quoteId', label: 'ใบเสนอราคาที่อ้างอิง' },
      { key: 'terms', label: 'เงื่อนไข', type: 'textarea' },
      { key: 'destination', label: 'สถานที่ส่งมอบ' },
      { key: 'summary', label: 'ข้อมูลที่ต่างจากใบเสนอราคา', type: 'textarea' },
    ],
    actions: [
      { key: 'submit', label: 'ส่ง PO' },
      { key: 'reconcile_po', label: 'เทียบกับใบเสนอราคา' },
    ],
  },
  payments: {
    kind: 'receipt',
    title: 'หลักฐานการชำระเงิน',
    fields: [
      { key: 'label', label: 'อ้างอิงหลักฐาน' },
      { key: 'amount', label: 'จำนวนเงิน', type: 'decimal' },
      { key: 'source', label: 'หลักฐานธนาคาร / บัญชี' },
      { key: 'summary', label: 'หมายเหตุสำหรับฝ่ายการเงิน', type: 'textarea' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งหลักฐาน รอการตรวจสอบ' },
      { key: 'verify_receipt', label: 'ตรวจสอบการรับเงินจริง' },
    ],
  },
  commitments: {
    kind: 'commitment',
    title: 'อนุญาตข้อผูกพันแยกต่างหาก',
    fields: [
      { key: 'label', label: 'ชิ้นส่วนและแบบฉลากฉบับที่เลือก' },
      { key: 'quantity', label: 'จำนวน', type: 'decimal' },
      { key: 'costCap', label: 'วงเงิน', type: 'decimal' },
      { key: 'source', label: 'หลักฐาน' },
      { key: 'summary', label: 'เจ้าของส่วนเกิน เวลา และผลเมื่อยกเลิก', type: 'textarea' },
    ],
    actions: [
      { key: 'submit', label: 'ส่งคำขอ' },
      { key: 'authorize_commitment', label: 'อนุญาตขอบเขตข้อผูกพัน' },
    ],
  },
  tasks: {
    kind: 'task',
    title: 'งานถัดไปที่มีผู้รับผิดชอบ',
    fields: [
      { key: 'label', label: 'งาน' },
      { key: 'nextAction', label: 'ขั้นตอนถัดไป' },
      { key: 'dueAt', label: 'กำหนดวัน' },
      { key: 'summary', label: 'ผู้รับผิดชอบและขอบเขต', type: 'textarea' },
    ],
    actions: [{ key: 'submit', label: 'มอบหมายงาน' }],
  },
  conversations: {
    kind: 'conversation',
    title: 'บทสนทนาที่รู้ว่าใครเห็น',
    fields: [
      { key: 'label', label: 'หัวข้อ' },
      { key: 'summary', label: 'ข้อความที่ลูกค้าเห็น', type: 'textarea' },
    ],
    actions: [{ key: 'submit', label: 'บันทึกข้อความ' }],
  },
  imports: {
    kind: 'import_batch',
    title: 'พักข้อมูลต้นฉบับเพื่อทบทวน',
    fields: [
      { key: 'label', label: 'ชุดข้อมูลสังเคราะห์' },
      { key: 'source', label: 'source hash / version' },
      { key: 'summary', label: 'duplicate / grade / mapping / disposition', type: 'textarea' },
    ],
    actions: [{ key: 'submit', label: 'ส่งเพื่อทบทวน mapping' }],
  },
};
const extraForms: Record<string, [OemKind, string]> = {
  materials: ['material', 'วัตถุดิบและเกรด'],
  material_specs: ['material_spec', 'สเปกวัตถุดิบและ constituents'],
  ingredients: ['ingredient', 'ส่วนประกอบและ INCI'],
  criteria: ['criterion', 'เกณฑ์ sensory ที่ตกลง'],
  components: ['component', 'ชิ้นส่วนบรรจุภัณฑ์'],
  specifications: ['component_spec', 'สเปกชิ้นส่วนตามเวอร์ชัน'],
  processes: ['process', 'วิธีและกระบวนการ'],
  densities: ['density', 'Density ที่วัดและตรวจทาน'],
  sample_requests: ['sample_request', 'คำขอทำตัวอย่าง'],
  preparations: ['preparation', 'ทะเบียนการเตรียมจริง'],
  dispatches: ['dispatch', 'การส่งและรับตัวอย่าง'],
  targets: ['target', 'รายการที่เลือกและผลกระทบ'],
  feasibilities: ['feasibility', 'ความเป็นไปได้และกำลังการผลิต'],
  applicabilities: ['applicability', 'Applicability และเหตุผล'],
  test_plans: ['test_plan', 'วิธีทดสอบและเกณฑ์'],
  holds: ['hold', 'ประเด็นและการแก้ hold'],
  policies: ['policy', 'นโยบายและเงื่อนไข'],
  facts: ['fact', 'ข้อเท็จจริงที่ทบทวน'],
  proposals: ['proposal', 'เอกสารและ deck ที่ทบทวน'],
  exceptions: ['exception', 'Business exception'],
};
for (const [page, [kind, title]] of Object.entries(extraForms))
  forms[page] = {
    kind,
    title,
    fields: [],
    actions: [
      { key: 'submit', label: 'ส่งเพื่อทบทวน' },
      {
        key: kind === 'policy' || kind === 'exception' ? 'review_commercial' : 'review_technical',
        label: 'ตรวจตามหน้าที่',
      },
    ],
  };
forms.packaging_records = requiredValue(forms.packaging);
forms.artwork_records = {
  kind: 'artwork',
  title: 'ทะเบียนแบบฉลากและไฟล์ฉบับที่เลือก',
  fields: [],
  actions: [
    { key: 'submit', label: 'ส่ง proof' },
    { key: 'review_technical', label: 'ทบทวนเทคนิค' },
    { key: 'review_regulatory', label: 'ทบทวน QA' },
    { key: 'decide_design', label: 'รับดีไซน์' },
  ],
};
forms.sample_records = {
  kind: 'sample',
  title: 'ทะเบียนตัวอย่างจริงและการจัดส่ง',
  fields: [],
  actions: [{ key: 'submit', label: 'ส่งทะเบียนให้ทบทวน' }],
};
const actionFields: Record<string, Record<string, FieldContract>> = {
  prepare: {
    preparationCode: { type: 'string' },
    quantity: { type: 'string' },
    unit: { enum: ['g', 'kg', 'mL', 'L', 'ea'] },
    conditions: { type: 'string' },
    custodian: { type: 'string' },
    retainedReference: { type: 'string' },
  },
  dispatch: { dispatchReference: { type: 'string' }, recipient: { type: 'string' } },
  allocate_payment: {
    receiptId: { type: 'string', format: 'uuid' },
    quoteId: { type: 'string', format: 'uuid' },
    amount: { type: 'string' },
  },
  reverse_allocation: {
    allocationId: { type: 'string' },
    source: { type: 'string' },
    reason: { type: 'string' },
  },
};
export function RecordEditor({
  definition,
  projectId,
  records,
  skus = [],
  audience = 'staff',
  role,
  readBundles = [],
  actions,
  userId,
  decisions,
  busy,
  online = true,
  onSave,
  onAction,
}: {
  readonly definition: FormDefinition;
  readonly projectId?: string;
  readonly records: OemRecord[];
  readonly skus?: readonly {
    id: string;
    label: string;
  }[];
  readonly audience?: 'customer' | 'staff';
  readonly role?: string;
  readonly readBundles?: readonly string[];
  readonly userId?: string;
  readonly decisions?: {
    recordId: string;
    action: string;
  }[];
  readonly actions: readonly string[];
  readonly busy: boolean;
  readonly online?: boolean;
  readonly onSave: (
    kind: OemKind,
    content: Record<string, unknown>,
    target: OemRecord | null,
    revision?: boolean,
    skuId?: string | null,
  ) => Promise<void>;
  readonly onAction: (
    action: string,
    record: OemRecord,
    payload: Record<string, unknown>,
  ) => Promise<void>;
}) {
  const [selectedSku, setSelectedSku] = useState('');
  const authorable =
    (role !== 'production' || definition.kind === 'feasibility') &&
    readBundles.includes(oemBundle(definition.kind)) &&
    (audience === 'staff' ||
      ['brief', 'evaluation', 'annotation', 'artwork', 'po', 'receipt', 'conversation'].includes(
        definition.kind,
      ));
  const rows = records.filter((r) => r.kind === definition.kind);
  const [selected, setSelected] = useState('new');
  const [values, setValues] = useResumableFields(
    `${userId}:${projectId}:${audience}:${role}:${definition.kind}`,
  );
  const [error, setError] = useState('');
  const [budgetError, setBudgetError] = useState('');
  const [action, setAction] = useState('');
  const [payload, setPayload] = useState<Record<string, unknown>>({});
  const current = rows.find((r) => r.id === selected);
  const guidedBrief = audience === 'customer' && definition.kind === 'brief';
  const briefBudget = customerBriefBudget(values);
  function updateGuidedValues(next: Record<string, unknown>): boolean {
    const budget = customerBriefBudget(next);
    if (!budget.valid) {
      setBudgetError(
        `เพิ่มข้อมูลนี้ไม่ได้ ข้อความบรีฟรวมเกิน ${-budget.remaining} ตัวอักษร กรุณาลดรายละเอียด บริการ หรือลิงก์ก่อน · ข้อมูลเดิมยังอยู่ครบ`,
      );
      return false;
    }
    setBudgetError('');
    setValues(next);
    return true;
  }
  const savedValues =
    current &&
    (guidedBrief
      ? customerBriefEquivalent(values, current.content)
      : JSON.stringify(values) === JSON.stringify(current.content));
  const canAuthor =
    authorable &&
    actions.includes('create') &&
    (!current || audience === 'staff' || current.createdBy === userId);
  const draftWritable =
    !current ||
    (current.state === 'draft' &&
      !decisions?.some((d) => d.recordId === current.id && d.action === 'submit') &&
      !rows.some((r) => r.rootId === current.rootId && r.version > current.version));
  const contract =
    definition.kind === 'cost'
      ? (oemCostRequestContract as FieldContract)
      : (oemContentContracts as Record<string, FieldContract>)[definition.kind];
  const properties = contract?.properties ?? {};
  function select(id: string) {
    setSelected(id);
    setSelectedSku(rows.find((r) => r.id === id)?.skuId ?? '');
    const content = structuredClone(rows.find((r) => r.id === id)?.content ?? {});
    setValues(
      definition.kind === 'cost'
        ? Object.fromEntries(Object.entries(content).filter(([k]) => k in properties))
        : guidedBrief
          ? hydrateCustomerBrief(content)
          : content,
    );
    setError('');
    setBudgetError('');
  }
  const ordered = [...definition.fields.map((f) => f.key), ...Object.keys(properties)].filter(
    (k, i, a) => a.indexOf(k) === i && properties[k],
  );
  const available = [
    ...definition.actions,
    ...(definition.kind === 'sample_request'
      ? [{ key: 'prepare', label: 'อนุญาตและบันทึกการเตรียม' }]
      : []),
    ...(definition.kind === 'sample'
      ? [
          { key: 'dispatch', label: 'ส่งตัวอย่าง' },
          { key: 'receive', label: 'รับตัวอย่าง' },
        ]
      : []),
    ...(definition.kind === 'target'
      ? [{ key: 'adopt_target', label: 'ยอมรับรายการที่เลือกและผลกระทบ' }]
      : []),
    ...(definition.kind === 'receipt'
      ? [
          { key: 'verify_receipt', label: 'ตรวจหลักฐานเงินจริง' },
          { key: 'allocate_payment', label: 'จัดสรรเงินจริง' },
        ]
      : []),
    ...(definition.kind === 'allocation'
      ? [{ key: 'reverse_allocation', label: 'กลับรายการพร้อมหลักฐาน' }]
      : []),
  ].filter(
    (a) =>
      actions.includes(a.key) &&
      (!a.key.startsWith('review_') || current?.createdBy !== userId) &&
      (a.key !== 'accept_quote' ||
        decisions?.some((d) => d.recordId === current?.id && d.action === 'issue_quote')) &&
      (a.key !== 'submit' ||
        (canAuthor &&
          current &&
          !decisions?.some((d) => d.recordId === current.id && d.action === 'submit') &&
          !rows.some((r) => r.rootId === current.rootId && r.version > current.version))),
  );
  const history = (
    <aside className="card">
      <h2>{guidedBrief ? 'ฉบับที่บันทึก' : 'เวอร์ชันและขอบเขต'}</h2>
      {rows.length ? (
        rows.map((r) => (
          <button
            key={r.id}
            className={`queue-item ${selected === r.id ? 'active' : ''}`}
            onClick={() => select(r.id)}
          >
            <strong>{displayValue(r.content.label ?? r.kind)}</strong>
            <span>
              v{r.version} · {stateLabel(r.state)}
            </span>
            <span className="version">{r.hash.slice(0, 12)}</span>
          </button>
        ))
      ) : (
        <p className="muted">ยังไม่มีข้อมูล · บันทึกร่างที่ยังไม่ครบได้</p>
      )}
    </aside>
  );
  return (
    <>
      <div className="page-title">
        <div>
          <h1>{definition.title}</h1>
          <p className="muted">
            {guidedBrief
              ? 'เลือกแนวคิด บอกความต้องการ แล้วบันทึกร่างก่อนส่งให้ทีมงาน'
              : 'ข้อมูลที่ขาดยังไม่มีผลตรวจ · ฉบับที่ส่งแล้วต้องแก้ไขเป็นฉบับใหม่พร้อมเหตุผล'}
          </p>
        </div>
        {canAuthor ? (
          <button className="action secondary" onClick={() => select('new')}>
            สร้างร่างใหม่
          </button>
        ) : null}
      </div>
      <div className={guidedBrief ? 'guided-brief-layout' : 'review-layout'}>
        {!guidedBrief ? history : null}
        <section className="card">
          <h2>{current ? displayValue(current.content.label) : 'ร่างใหม่'}</h2>
          {definition.kind === 'brief' &&
          audience === 'staff' &&
          actions.includes('create') &&
          !current &&
          projectId ? (
            <button
              type="button"
              onClick={() =>
                void request('/initial-request/' + projectId).then(
                  (value) => {
                    if (value) {
                      const initial = value as { label: string; summary: string };
                      setValues((previous) => ({
                        ...previous,
                        label: initial.label,
                        summary: initial.summary,
                      }));
                    } else setError('ยังไม่มีคำขอเริ่มต้นที่บันทึกไว้');
                  },
                  () => setError('อ่านคำขอเริ่มต้นไม่ได้ ตรวจสอบสิทธิ์'),
                )
              }
            >
              นำคำขอเริ่มต้นที่บันทึกไว้มาเขียนบรีฟ
            </button>
          ) : null}
          {!authorable ? (
            <p className="scope">
              พื้นที่นี้อ่านและตัดสินใจได้เฉพาะขอบเขตที่ได้รับสิทธิ์
              การแก้ไขเป็นหน้าที่ของผู้รับผิดชอบ
            </p>
          ) : null}
          <form
            className="form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              if (!canAuthor || !draftWritable || busy || (guidedBrief && !briefBudget.valid))
                return;
              const saveValues = guidedBrief ? customerBriefForSave(values) : values;
              const parsed =
                definition.kind === 'cost'
                  ? oemCostRequestContentSchema.safeParse(values)
                  : validateOemContent(definition.kind, saveValues);
              if (!parsed.success) {
                setError(
                  guidedBrief
                    ? 'กรุณาตรวจข้อมูลที่กรอก เช่น จำนวน และรายละเอียดเพิ่มเติม'
                    : parsed.error.issues
                        .map((i) => i.path.join('.') + ': ' + i.message)
                        .join(' · '),
                );
                return;
              }
              void onSave(definition.kind, saveValues, current ?? null, false, selectedSku || null);
            }}
          >
            <fieldset disabled={!canAuthor}>
              {!guidedBrief ? (
                <label>
                  {guidedBrief ? 'สินค้าที่เกี่ยวข้อง' : 'ขอบเขต SKU'}
                  <select
                    value={selectedSku}
                    disabled={Boolean(current)}
                    onChange={(e) => setSelectedSku(e.target.value)}
                  >
                    <option value="">
                      {guidedBrief
                        ? 'ทั้งโครงการ · ยังไม่แยกรายสินค้า'
                        : 'ระดับโปรเจกต์ · ไม่ผูก SKU'}
                    </option>
                    {skus.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}
              {guidedBrief ? <ProductPicker values={values} onChange={updateGuidedValues} /> : null}
              {guidedBrief ? (
                <>
                  <p className="fineprint" role="status">
                    ข้อความบรีฟรวมแนวคิด บริการ และลิงก์ เหลือ {briefBudget.remaining} ตัวอักษร จาก
                    4000
                  </p>
                  {budgetError ? (
                    <p className="note-danger" role="alert">
                      {budgetError}
                    </p>
                  ) : null}
                </>
              ) : null}
              {guidedBrief ? <h3>2. บอกความต้องการ</h3> : null}
              {(guidedBrief
                ? ordered.filter((k) =>
                    [
                      'label',
                      'summary',
                      'market',
                      'quantity',
                      'fillSize',
                      'requestedServices',
                    ].includes(k),
                  )
                : ordered
              ).map((k) =>
                k === 'stockSourceRef' &&
                projectId &&
                (definition.kind === 'material_spec' || definition.kind === 'component_spec') ? (
                  <StockSourcePicker
                    key={k + (userId ?? '') + projectId}
                    projectId={projectId}
                    kind={definition.kind}
                    value={values[k]}
                    onChange={(v) => {
                      const next = { ...values };
                      if (v === undefined) delete next.stockSourceRef;
                      else next[k] = v;
                      setValues(next);
                    }}
                  />
                ) : (
                  <ContractField
                    key={k}
                    name={k}
                    schema={properties[k] ?? { type: 'string' }}
                    labelOverride={
                      guidedBrief && k === 'requestedServices' ? 'บริการที่สนใจ' : undefined
                    }
                    value={
                      guidedBrief && k === 'summary'
                        ? productChoiceNotes(values.summary)
                        : values[k]
                    }
                    records={records}
                    skus={skus}
                    onChange={(v) =>
                      guidedBrief && k === 'requestedServices'
                        ? updateGuidedValues(
                            updateCustomerServices(
                              values,
                              Array.isArray(v)
                                ? v.filter((item): item is string => typeof item === 'string')
                                : [],
                            ),
                          )
                        : (guidedBrief ? updateGuidedValues : setValues)({
                            ...values,
                            [k]:
                              guidedBrief && k === 'summary'
                                ? updateProductNotes(values.summary, typeof v === 'string' ? v : '')
                                : v,
                          })
                    }
                  />
                ),
              )}
              {guidedBrief ? (
                <details className="brief-advanced">
                  <summary>รายละเอียดเพิ่มเติมและเหตุผลการแก้ไข</summary>{' '}
                  <label>
                    {guidedBrief ? 'สินค้าที่เกี่ยวข้อง' : 'ขอบเขต SKU'}
                    <select
                      value={selectedSku}
                      disabled={Boolean(current)}
                      onChange={(e) => setSelectedSku(e.target.value)}
                    >
                      <option value="">
                        {guidedBrief
                          ? 'ทั้งโครงการ · ยังไม่แยกรายสินค้า'
                          : 'ระดับโปรเจกต์ · ไม่ผูก SKU'}
                      </option>
                      {skus.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </label>
                  {ordered
                    .filter(
                      (k) =>
                        ![
                          'label',
                          'summary',
                          'market',
                          'quantity',
                          'fillSize',
                          'requestedServices',
                        ].includes(k),
                    )
                    .map((k) => (
                      <ContractField
                        key={k}
                        name={k}
                        schema={properties[k] ?? { type: 'string' }}
                        value={values[k]}
                        records={records}
                        skus={skus}
                        onChange={(v) => setValues({ ...values, [k]: v })}
                      />
                    ))}
                </details>
              ) : null}
              {guidedBrief ? (
                <p className="scope">
                  3. บันทึกร่างก่อน แล้วเลือกฉบับที่บันทึกในประวัติเพื่อส่งให้ทีมงานเมื่อพร้อม
                </p>
              ) : null}
              <div className="actions">
                <button
                  className="action"
                  disabled={
                    busy || !canAuthor || !draftWritable || (guidedBrief && !briefBudget.valid)
                  }
                >
                  {guidedBrief ? 'บันทึกร่าง' : 'บันทึกร่างเวอร์ชัน'}
                </button>
                {current && canAuthor && actions.includes('revise') ? (
                  <button
                    className="action secondary"
                    type="button"
                    disabled={busy || !online || !values.revisionReason}
                    onClick={() =>
                      void onSave(definition.kind, values, current, true, selectedSku || null)
                    }
                  >
                    สร้างฉบับใหม่ · เก็บประวัติเดิม
                  </button>
                ) : null}
              </div>
            </fieldset>
          </form>
          {definition.kind === 'cost' && current ? (
            <section aria-label="ผลคำนวณจากแหล่งอ้างอิง">
              <h3>ผลคำนวณที่ตรึงโดยเซิร์ฟเวอร์</h3>
              <p>
                แก้ไขได้เฉพาะรายการที่เลือก นโยบาย และแหล่งราคา · ผลคำนวณและรายการอ้างอิง
                สร้างใหม่โดยเซิร์ฟเวอร์
              </p>
              <pre className="contract-result">
                {JSON.stringify(
                  {
                    engineVersion: current.content.engineVersion,
                    state: current.content.computationState,
                    issues: current.content.issues,
                    result: current.content.result,
                  },
                  null,
                  2,
                )}
              </pre>
            </section>
          ) : null}
          {error ? (
            <p className="note-danger" role="alert">
              {error}
            </p>
          ) : null}
          {current ? (
            <div className="toolbar">
              {available.map((a) => (
                <button
                  key={a.key}
                  className="action secondary"
                  disabled={busy || !online || (guidedBrief && a.key === 'submit' && !savedValues)}
                  onClick={() => {
                    setAction(a.key);
                    setPayload(
                      a.key.startsWith('review_')
                        ? { disposition: 'pending', comment: '' }
                        : { comment: '' },
                    );
                  }}
                >
                  {guidedBrief && a.key === 'submit' ? 'ส่งให้ทีมงาน' : a.label}
                </button>
              ))}
            </div>
          ) : null}
          {guidedBrief && current && !savedValues ? (
            <p className="scope">มีข้อมูลที่ยังไม่ได้บันทึก กรุณาบันทึกร่างก่อนส่งเวอร์ชันนี้</p>
          ) : null}
          <p className="scope">การบันทึกร่างไม่ใช่การอนุมัติ การรับชำระ หรือการยืนยันคำสั่งซื้อ</p>
        </section>
        {guidedBrief ? (
          <details className="brief-history">
            <summary>ประวัติและฉบับที่บันทึก</summary>
            {history}
          </details>
        ) : null}
      </div>
      {action && current ? (
        <MediaDialog
          className="oem-dialog"
          label={`ขอบเขต ${action}`}
          onDismiss={() => setAction('')}
        >
          <h2>
            {guidedBrief && action === 'submit'
              ? 'ส่งให้ทีมงาน'
              : available.find((a) => a.key === action)?.label}
          </h2>
          <p>
            {displayValue(current.content.label)} v{current.version} · {current.hash}
          </p>
          <form
            className="form-grid"
            onSubmit={(e) => {
              e.preventDefault();
              if (
                busy ||
                !online ||
                !available.some((a) => a.key === action) ||
                (guidedBrief && action === 'submit' && !savedValues)
              )
                return;
              const parsed = validateOemPayload(action, payload);
              if (!parsed.success) {
                setError('ข้อมูลการตัดสินใจไม่ครบตามขอบเขต');
                return;
              }
              void onAction(action, current, payload).then(() => setAction(''));
            }}
          >
            {Object.entries(
              actionFields[action] ??
                (action.startsWith('review_')
                  ? {
                      disposition: { enum: ['pending', 'passed', 'failed', 'not_applicable'] },
                      comment: { type: 'string' },
                    }
                  : { comment: { type: 'string' } }),
            ).map(([k, s]) => (
              <ContractField
                key={k}
                name={k}
                schema={s}
                value={payload[k]}
                records={records}
                required
                onChange={(v) => setPayload({ ...payload, [k]: v })}
              />
            ))}
            <button className="action" disabled={busy}>
              ตรวจและบันทึกตามขอบเขต
            </button>
          </form>
          <button className="action secondary" onClick={() => setAction('')}>
            ยกเลิก
          </button>
        </MediaDialog>
      ) : null}
    </>
  );
}
